import { P2GText } from '@parcel2go/core-ui';
import { useEffect, useState } from 'react';
import { FilterItem, FilterSelection } from '../../../lib/helpers/SmartSendFilterApplier';
import AlertWithIcon from '../../AlertWithIcon';
import FilterOptionsGridCheckbox from './FilterOptionsGridCheckbox';
import FilterOptionsGridDrop from './FilterOptionsGridDrop';
import FilterOptionsGridIconCheckbox from './FilterOptionsGridIconCheckbox';
import FilterOptionsGridImage from './FilterOptionsGridImage';

interface props {
  filterItem: FilterItem;
  selection: FilterSelection;
  onSave: (included: string[], excluded: string[]) => void;
}

export interface FilterOptionsGridProps {
  filterItem: FilterItem;
  toggleSelected: (selection: string) => void;
  selected: string[];
  mode: FilterOptionsGridMode;
  setMode: (mode: FilterOptionsGridMode) => void;
  clearSelected: () => void;
  isSelected: (v: string) => boolean;
  search: string;
  setSearch: (search: string) => void;
  limit: number;
  setLimit: (limit: number) => void;
  selectAll: () => void;
}

export type FilterOptionsGridMode = 'INC' | 'EXC';

export default function FilterOptionsGrid(props: props) {
  const [selected, setSelected] = useState((props.selection?.Include || []).concat(props.selection?.Exclude || []));
  const [mode, setMode] = useState<FilterOptionsGridMode>(props.selection?.Exclude?.length > 0 ? 'EXC' : 'INC');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(10);
  const isSelected = (a: string) => selected.includes(a);
  const toggleSelected = (a: string) => setSelected(isSelected(a) ? selected.filter((s) => s !== a) : [...selected, a]);
  const selectAll = () => setSelected(props.filterItem.options.map((x) => x.value));
  const clearSelected = () => setSelected([]);

  useEffect(() => {
    save();
  }, [selected, mode]);

  const save = () => {
    if (mode === 'EXC') {
      props.onSave([], selected);
    } else {
      props.onSave(selected, []);
    }
  };

  if (props.filterItem.options.length === 0) {
    return (
      <AlertWithIcon>
        <P2GText>There are no options for this filter.</P2GText>
      </AlertWithIcon>
    );
  }

  if (props.filterItem.style === 'DROP') {
    return (
      <FilterOptionsGridDrop
        search={search}
        setSearch={setSearch}
        isSelected={isSelected}
        selectAll={selectAll}
        selected={selected}
        clearSelected={clearSelected}
        filterItem={props.filterItem}
        limit={limit}
        mode={mode}
        setMode={setMode}
        setLimit={setLimit}
        toggleSelected={toggleSelected}
      />
    );
  }

  if (props.filterItem.style === 'CHECKBOX') {
    return (
      <FilterOptionsGridCheckbox
        search={search}
        setSearch={setSearch}
        isSelected={isSelected}
        selectAll={selectAll}
        selected={selected}
        clearSelected={clearSelected}
        filterItem={props.filterItem}
        limit={limit}
        mode={mode}
        setMode={setMode}
        setLimit={setLimit}
        toggleSelected={toggleSelected}
      />
    );
  }

  if (props.filterItem.style === 'ICONCHECKBOX') {
    return (
      <FilterOptionsGridIconCheckbox
        search={search}
        setSearch={setSearch}
        isSelected={isSelected}
        selectAll={selectAll}
        selected={selected}
        clearSelected={clearSelected}
        filterItem={props.filterItem}
        limit={limit}
        mode={mode}
        setMode={setMode}
        setLimit={setLimit}
        toggleSelected={toggleSelected}
      />
    );
  }

  return (
    <FilterOptionsGridImage
      search={search}
      setSearch={setSearch}
      isSelected={isSelected}
      selectAll={selectAll}
      selected={selected}
      clearSelected={clearSelected}
      filterItem={props.filterItem}
      limit={limit}
      mode={mode}
      setMode={setMode}
      setLimit={setLimit}
      toggleSelected={toggleSelected}
    />
  );
}
